import posthog from "posthog-js";
import type { BeforeSendFn } from "posthog-js";

const COOKIEYES_ANALYTICS_CATEGORY = "analytics";
const COOKIEYES_BANNER_LOAD_EVENT = "cookieyes_banner_load";
const COOKIEYES_CONSENT_EVENT = "cookieyes_consent_update";

// Exceptions we can neither act on nor symbolicate: errors raised by third-party scripts,
// and browser noise that carries no stack trace. Left in, they bury real issues and skew
// PostHog's unresolved source map stats.
const IGNORED_EXCEPTION_SOURCES = ["salesmanago.com"];
const IGNORED_EXCEPTION_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  "Script error.",
  "AbortError:",
];

type ExceptionFrame = { filename?: string };
type Exception = { value?: string; stacktrace?: { frames?: ExceptionFrame[] } };

const isIgnoredException = ({ value = "", stacktrace }: Exception) =>
  IGNORED_EXCEPTION_MESSAGES.some((message) => value.startsWith(message)) ||
  IGNORED_EXCEPTION_SOURCES.some(
    (source) => value.includes(source) || (stacktrace?.frames ?? []).some(({ filename }) => filename?.includes(source))
  );

export const dropIgnoredExceptions: BeforeSendFn = (event) => {
  if (event?.event !== "$exception") {
    return event;
  }
  const exceptions: Exception[] = event.properties.$exception_list ?? [];
  return exceptions.some(isIgnoredException) ? null : event;
};

// CookieYes reports consent asynchronously, so "not yet reported" has to stay distinct
// from "denied": under the US opt-out banner a visitor who never touches the banner has
// granted consent, and treating the gap as a denial would drop them entirely.
type ConsentState = "granted" | "denied" | "pending";

let projectToken: string | null = null;
let initialized = false;
let analyticsConsentGranted = false;

const getCookieValue = (name: string) => {
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));
  return cookie?.slice(prefix.length) ?? "";
};

const readCategoryConsent = (categories?: Record<string, boolean>): ConsentState => {
  const analytics = categories?.[COOKIEYES_ANALYTICS_CATEGORY];
  if (analytics === undefined) {
    return "pending";
  }
  return analytics ? "granted" : "denied";
};

const getAnalyticsConsent = (): ConsentState => {
  if (!process.env.NEXT_PUBLIC_COOKIEYES_CLIENT_ID) {
    return "granted";
  }

  const cookieYes = window.getCkyConsent?.();
  if (cookieYes) {
    return readCategoryConsent(cookieYes.categories);
  }

  // getCkyConsent() only exists once the banner has loaded. A persisted cookie can still
  // grant consent before then, but its absence is inconclusive rather than a denial —
  // we wait for cookieyes_banner_load instead of opting out.
  const grantedByCookie = getCookieValue("cookieyes-consent")
    .split(",")
    .some((value) => value.trim() === `${COOKIEYES_ANALYTICS_CATEGORY}:yes`);

  return grantedByCookie ? "granted" : "pending";
};

const enablePostHog = () => {
  analyticsConsentGranted = true;
  if (!projectToken) {
    return;
  }

  if (!initialized) {
    posthog.init(projectToken, {
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      defaults: "2026-05-30",
      capture_exceptions: true,
      capture_pageview: false,
      before_send: dropIgnoredExceptions,
    });
    initialized = true;
  }

  posthog.opt_in_capturing({ captureEventName: false });
};

const disablePostHog = () => {
  analyticsConsentGranted = false;
  if (initialized) {
    posthog.opt_out_capturing();
  }
};

const applyConsent = (consent: ConsentState) => {
  if (consent === "granted") {
    enablePostHog();
  } else if (consent === "denied") {
    disablePostHog();
  }
  // "pending" deliberately leaves PostHog untouched until CookieYes reports.
};

const syncConsent = () => {
  applyConsent(getAnalyticsConsent());
};

const onBannerLoad = (event: Event) => {
  const { categories } = (event as CustomEvent<CookieYesConsentDetail>).detail ?? {};
  const consent = readCategoryConsent(categories);
  if (consent === "pending") {
    syncConsent();
    return;
  }
  applyConsent(consent);
};

const onConsentUpdate = (event: Event) => {
  const { accepted = [], rejected = [] } = (event as CustomEvent<CookieYesConsentDetail>).detail ?? {};
  if (accepted.includes(COOKIEYES_ANALYTICS_CATEGORY)) {
    enablePostHog();
  } else if (rejected.includes(COOKIEYES_ANALYTICS_CATEGORY)) {
    disablePostHog();
  } else {
    syncConsent();
  }
};

export const setupPostHog = (token: string) => {
  projectToken = token;
  syncConsent();
  document.addEventListener(COOKIEYES_BANNER_LOAD_EVENT, onBannerLoad);
  document.addEventListener(COOKIEYES_CONSENT_EVENT, onConsentUpdate);

  return () => {
    document.removeEventListener(COOKIEYES_BANNER_LOAD_EVENT, onBannerLoad);
    document.removeEventListener(COOKIEYES_CONSENT_EVENT, onConsentUpdate);
  };
};

export const capturePostHogEvent = (eventName: string, properties?: Record<string, any>) => {
  if (initialized && analyticsConsentGranted) {
    posthog.capture(eventName, properties);
  }
};

export const identifyPostHogUser = (userId: string, properties?: Record<string, any>) => {
  if (initialized && analyticsConsentGranted) {
    posthog.identify(userId, properties);
  }
};

export const resetPostHog = () => {
  if (initialized) {
    posthog.reset();
  }
};
